import { axiosInstance, axiosJsonInstance } from "@/axios.instance";
import { AxiosInstance } from "axios";
import { QuickEndSettingsDto } from "@/types/organization-settings/QuickEndSettings";
import { TenantSystemPrefs } from "@/store/types";
import { AlarmTag, EventQueuePriorityColors } from "@/store/eventqueue/types";
import { PaginatedSearchRequest } from "@/mixins/PaginatedSearch";
import { AreaSearchAdditionalFieldsSettingResponse } from "@/types/area-tree-select/AreaSearchAdditionalFieldsSettingResponse";
import AlarmTagsPagedResponse from "@/types/sv-data/alarms/AlarmTagsPagedResponse";
import { NotificationSubjectLineDto } from "@/types/organization-settings/NotificationSubjectLineSettings";
import { MediaMatrixLayoutPagedResponse, MediaMatrixLayout } from "@/types/organization-settings/MediaMatrixLayout";
import { HttpStatusCode } from "@/utils/HttpStatusCode";

const OrganizationQuickEndSettingsUrl = "/OrganizationSettings/QuickEnd";
const OrganizationSettingsURL = "/OrganizationSettings";

class OrganizationApi {
	private axios: AxiosInstance;
	private axiosJson: AxiosInstance;

	constructor() {
		this.axios = axiosInstance;
		this.axiosJson = axiosJsonInstance;
	}

	public async retrieveTenantSystemPrefs(liveValue: boolean) : Promise<TenantSystemPrefs> {
		const { data } = await this.axiosJson.get(`${OrganizationSettingsURL}/TenantPrefs?liveValue=${liveValue}`);
		return data;
	}

	public async retrieveAreaSearchAdditionalFieldsSetting(liveValue: boolean): Promise<AreaSearchAdditionalFieldsSettingResponse[]> {
		const { data } = await this.axiosJson.get(`${OrganizationSettingsURL}/TenantPrefs/AdditionalAreaSearchFields?liveValue=${liveValue}`);
		return data;
	}

	public async createOrUpdateTenantSystemPrefs(tenantSystemPrefs: TenantSystemPrefs) : Promise<TenantSystemPrefs> {
		const { data } = await this.axiosJson.put(`${OrganizationSettingsURL}/TenantPrefs`, tenantSystemPrefs);
		return data;
	}

	public async retrieveQuickEndSettings(groupID: number, liveValue: boolean) : Promise<QuickEndSettingsDto> {
		const { data, status } = await this.axios.get(`${OrganizationQuickEndSettingsUrl}/?groupId=${groupID}&liveValue=${liveValue}`);
		if (status == 204){
			return null;
		}

		return data;
	}

	public async createOrUpdateQuickEndSettings(quickEndSettingsRequest: QuickEndSettingsDto) : Promise<QuickEndSettingsDto> {
		const { data } = await this.axiosJson.put(OrganizationQuickEndSettingsUrl, quickEndSettingsRequest);
		return data;
	}

	public async deleteQuickEndSettings() : Promise<void> {
		await this.axios.delete(OrganizationQuickEndSettingsUrl);
	}

	public async createAlarmQueuePriorityColor(eventQueuePriorityColor: EventQueuePriorityColors): Promise<EventQueuePriorityColors>{
		const { data } = await this.axiosJson.post(`${OrganizationSettingsURL}/EventQueuePriorityColors`, eventQueuePriorityColor);
		return data;
	}

	public async retrieveAlarmQueuePriorityColors(paginatedSearchRequest: PaginatedSearchRequest): Promise<any>{
		const { data } = await this.axios.get(`${OrganizationSettingsURL}/EventQueuePriorityColors`, paginatedSearchRequest);
		return data;
	}

	public async updateAlarmQueuePriorityColor(eventQueuePriorityColor: EventQueuePriorityColors): Promise<EventQueuePriorityColors>{
		const { data } = await this.axiosJson.put(`${OrganizationSettingsURL}/EventQueuePriorityColors`, eventQueuePriorityColor);
		return data;
	}

	public async deleteAlarmQueuePriorityColor(eventQueuePriorityColorId: number): Promise<EventQueuePriorityColors>{
		const { data } = await this.axios.delete(`${OrganizationSettingsURL}/EventQueuePriorityColors/${eventQueuePriorityColorId}`);
		return data;
	}

	public async validateAlarmQueuePriorityColors(eventQueuePriorityColor: EventQueuePriorityColors): Promise<boolean> {
		const response = await this.axiosJson.post(`${OrganizationSettingsURL}/EventQueuePriorityColorsValidate`, eventQueuePriorityColor);
		return response.data;
	}

	public async retrieveAlarmTags(pageRequest: PaginatedSearchRequest): Promise<AlarmTagsPagedResponse> {
		const { data } = await this.axios.get(`/Tags/Alarm`, pageRequest);
		return data;
	}

	public async deleteAlarmTag(responseAlarmTypeID: number): Promise<boolean> {
		const { data } = await this.axios.delete(`/Tags/Alarm/${responseAlarmTypeID}`);
		return data;
	}

	public async createOrUpdateAlarmTag(alarmTag: AlarmTag): Promise<AlarmTag> {
		const { data } = await this.axiosJson.put(`/Tags/Alarm`, alarmTag);
		return data;
	}

	public async retrieveNotificationSubjectLine(groupID: number) : Promise<NotificationSubjectLineDto> {
		const { data } = await this.axios.get(`${OrganizationSettingsURL}/RetrieveNotificationSubjectLine?groupId=${groupID}`);
		return data;
	}

	public async createOrUpdateNotificationSubjectLine(notificationSubjectLine: NotificationSubjectLineDto) : Promise<NotificationSubjectLineDto> {
		const { data } = await this.axiosJson.put(`${OrganizationSettingsURL}/CreateOrUpdateNotificationSubjectLine`, notificationSubjectLine);
		return data;
	}

	public async GetMediaMatrixLayoutSettings(pageRequest: PaginatedSearchRequest): Promise<MediaMatrixLayoutPagedResponse> {
		const { data } = await this.axiosJson.get(`${OrganizationSettingsURL}/MediaMatrixLayoutSettings`, pageRequest);
		return data;
	}

	public async CreateOrUpdateMediaMatrixLayoutSettings(groupId: number, layoutIndex: number): Promise<number> {
		const { data } = await this.axiosJson.put(`${OrganizationSettingsURL}/CreateOrUpdateMediaMatrix?groupId=${groupId}&layoutIndex=${layoutIndex}`);
		return data;
	}

	public async GetMediaMatrixLayout(groupId: number): Promise<number> {
		const { data } = await this.axiosJson.get(`${OrganizationSettingsURL}/MediaMatrixLayout?groupId=${groupId}`);
		return data;
	}

	public async DeleteMediaMatrixLayoutSettings(groupId: number): Promise<boolean> {
		const { data } = await this.axiosJson.delete(`${OrganizationSettingsURL}/DeleteMediaMatrixLayout/${groupId}`);
		return data;
	}

	public async GetDefaultNotificationTemplateId(groupID: number): Promise<number | null> {
		const { data, status } = await this.axiosJson.get(`${OrganizationSettingsURL}/DefaultNotificationTemplateId?groupId=${groupID}`);
		if (status == HttpStatusCode.NoContent){
			return null;
		}
		return data;
	}
}

const organizationApi = new OrganizationApi();
export default organizationApi;
